import { Form, Formik } from 'formik'
import React, { useState } from 'react'
import * as Yup from 'yup'
import { Button } from '../../components/button'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { TextInput } from '../../components/inputs/textInput'
import ModalComponent from '../../components/modal'
import { TableComponent } from '../../components/table'
import { HeaderSubheader } from '../../components/typography/HeaderSubheader'
import {
  useCreateRoleMutation,
  useEditRoleMutation,
  useGetPermissionsQuery,
  useGetRolesQuery,
} from '../../redux-toolkits/user/user.slice'
import { type Role } from '../../redux-toolkits/user/user.type'
import type { Permission } from '../../redux-toolkits/auth/auth.type'
import { GroupedPermissionSelector } from './grouped-permission-selector'

const RolesSection: React.FC = () => {
  const [editing, setEditing] = useState<Role | undefined>(undefined)
  const [openModal, setOpenModal] = useState(false)

  const { data: roles, isLoading } = useGetRolesQuery({
    skip: 0,
    limit: 1000,
  })
  const [editRole, { isLoading: editingRole }] = useEditRoleMutation()

  const {
    data: permissions,
    isLoading: loadingPermissions,
    isError: permissionsFailed,
    refetch: refetchPermissions,
  } = useGetPermissionsQuery({
    skip: 0,
    limit: 1000,
  })

  const closeModal = (): void => {
    setOpenModal(false)
    setEditing(undefined)
  }

  return (
    <div>
      <div className="flex flex-col gap-10">
        <div className="flex gap-4 flex-wrap items-center">
          <HeaderSubheader
            title="Roles"
            text="Manage the roles and permissions of your team members."
          />

          <Button
            type="button"
            label="Create New Role"
            primary
            size="medium"
            onClick={() => {
              setOpenModal(true)
            }}
          />
        </div>

        <div className="w-full overflow-x-scroll">
          <TableComponent
            loading={isLoading}
            headers={[
              'Name',
              'Description',
              'Permission count',
              'Status',
              'Actions',
            ]}
            rows={
              roles?.data?.map((role) => {
                return {
                  id: role._id,
                  content: [
                    role.name ?? '',
                    role.description ?? '',
                    role.permissions.length.toLocaleString() ?? '',
                    <Status
                      key={`${role._id}-status`}
                      text={!role.active ? 'Inactive' : 'Active'}
                      type={!role.active ? 'fail' : 'success'}
                    />,
                    <Dropdown
                      className="text-left"
                      key={`${role._id}-controls`}
                      menuButton={
                        <Icon
                          id="ellipses"
                          height={18}
                          width={18}
                          className=""
                        />
                      }
                      closeOnClick={false}
                      menuItems={[
                        {
                          name: 'Edit',
                          value: '',
                          onClick: () => {
                            setEditing(role)
                            setOpenModal(true)
                          },
                        },
                        {
                          name: !role.active ? 'Activate' : 'Deactivate',
                          value: '',
                          onClick: async () => {
                            await editRole({
                              id: role._id,
                              payload: { active: !role.active },
                            })
                            closeModal()
                          },
                        },
                      ]}
                      loading={editingRole}
                    />,
                  ],
                }
              }) ?? []
            }
          />
        </div>
      </div>

      <CreateRoleModal
        role={editing}
        closeModal={closeModal}
        onSaved={() => {
          closeModal()
        }}
        permissions={permissions?.data ?? []}
        loadingPermissions={loadingPermissions}
        permissionsFailed={permissionsFailed}
        retryPermissions={() => {
          void refetchPermissions()
        }}
        open={openModal}
      />
    </div>
  )
}

const CreateRoleModal: React.FC<{
  open: boolean
  closeModal: () => void
  permissions: Permission[]
  loadingPermissions: boolean
  permissionsFailed: boolean
  retryPermissions: () => void
  onSaved: () => void
  role?: Role
}> = ({
  open,
  closeModal,
  permissions,
  loadingPermissions,
  permissionsFailed,
  retryPermissions,
  onSaved,
  role,
}) => {
  const [createRole, { isLoading: creatingRole }] = useCreateRoleMutation()
  const [editRole, { isLoading: updatingRole }] = useEditRoleMutation()

  return (
    <>
      <ModalComponent
        title={`${role !== undefined ? 'Edit' : 'Create new'} role`}
        open={open}
        setOpen={() => {}}
        onCloseCallback={() => {
          closeModal()
        }}
        childrenClassName="md:!max-w-[420px]"
      >
        <Formik
          initialValues={{
            name: role?.name ?? '',
            description: role?.description ?? '',
            permissions:
              role?.permissions.map((permission) => permission._id) ?? [],
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required('Name is required'),
            description: Yup.string().required('Description is required'),
          })}
          onSubmit={async (values) => {
            const result = role
              ? await editRole({ payload: values, id: role._id })
              : await createRole(values)

            if ('error' in result) return
            onSaved()
          }}
        >
          {({
            values,
            handleBlur,
            handleChange,
            errors,
            touched,
            setFieldValue,
          }) => (
            <Form className="flex flex-col gap-4">
              <TextInput
                type="text"
                name="name"
                placeholder="Role name"
                value={values.name}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={errors.name && touched.name ? errors.name : ''}
                hasIcon={false}
              />

              <TextInput
                type="textarea"
                name="description"
                placeholder="Role description"
                value={values.description}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.description && touched.description
                    ? errors.description
                    : ''
                }
                hasIcon={false}
              />

              <div>
                <HeaderSubheader
                  title="Permissions"
                  text="Select Permissions for this role"
                />

                {loadingPermissions ? (
                  <p className="mt-3 text-sm" role="status">
                    Loading permissions...
                  </p>
                ) : permissionsFailed ? (
                  <div className="mt-3 text-sm" role="alert">
                    <p>Unable to load permissions.</p>
                    <Button
                      type="button"
                      label="Retry"
                      onClick={retryPermissions}
                    />
                  </div>
                ) : (
                  <GroupedPermissionSelector
                    permissions={permissions}
                    selectedPermissions={values.permissions}
                    onChange={(selected) => {
                      void setFieldValue('permissions', selected)
                    }}
                  />
                )}
              </div>

              <Button
                disabled={
                  creatingRole ||
                  updatingRole ||
                  loadingPermissions ||
                  permissionsFailed
                }
                loading={creatingRole || updatingRole}
                type="submit"
                label="Save"
                primary
                className="sticky bottom-0"
              />
            </Form>
          )}
        </Formik>
      </ModalComponent>
    </>
  )
}

export default RolesSection
